import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  createHat as apiCreateHat,
  updateHatById as apiUpdateHat,
  deleteHatById as apiDeleteHat,
  listHats as apiListHats,
  listHatsPaginated as apiListHatsPaginated,
  addHatToFavorites as apiAddToFavorites,
  getUserFavorites as apiGetUserFavorites,
  removeHatFromFavorites as apiRemoveFromFavorites,
  removeHatFromDepartment as apiRemoveHatFromDepartment,
  modifyHatWithLLM as apiModifyHatWithLLM,
} from "../service/apiService";
import { toast } from "sonner";
import { useUser } from "./UserContext";

interface Hat {
  user_id?: string;
  id?: string;
  hat_name: string;
  department?: string;
  summary: string;
  initial_prompt: string;
  directions: string;
  is_public: boolean;
  model_id?: string;
  feature_keys: {
    image_creation: { enabled: boolean };
    internet_browsing: { enabled: boolean };
    kyva_llm: { enabled: boolean };
    kyva_connectors: { enabled: boolean; connector_ids?: string[]; }; 
    include_knowledge_base: {
      enabled: boolean;
      knowledge_base_ids: string[];
    };
  };
  updated_at?: string;
  isFavorite?: boolean;
  favorites?: any;
}

interface PaginationData {
  page: number;
  items_per_page: number;
  total_pages: number;
  has_next: boolean;
}

interface ErrorState {
  message: string;
  code?: number;
}

interface HatContextProps {
  hats: Hat[];
  hatsList: Hat[];
  selectedHat: Hat | null;
  setSelectedHat: (hat: Hat | null) => void;
  restoreSessionHat: (hatId?: string | null) => Promise<void>;
  fetchHats: (
    userId: string,
    page: number,
    itemsPerPage: number,
    viewType?: string
  ) => Promise<void>;
  fetchHatsList: () => Promise<void>;
  fetchHatsListWithFilter: (filterType: string) => Promise<void>; // New function
  createHat: (hatData: Hat) => Promise<any>;
  updateHatData: (hatId: string, updatedData: Partial<Hat>) => Promise<void>;
  deleteHatById: (hatId: string) => Promise<void>;
  removeHatFromDepartment: (hatId: string) => Promise<void>;
  modifyHatWithLLM: (hatId: string, instructions: string) => Promise<any>;
  loading: boolean;
  listLoading: boolean;
  error: ErrorState | null;
  pagination: PaginationData;
  setPage: (page: number) => void;
  setViewType: (viewType: string) => void;
  favoriteHats: string[];
  toggleFavorite: (hatId: string) => Promise<void>;
  resetError: () => void;
  viewType: string;
  initialPromptSent: boolean;
  setInitialPromptSent: (sent: boolean) => void;
  currentFilter: string;
  filterLocked: boolean;
  updateCurrentFilter: (filter: string) => void;
  currentDepartmentFilter: string | null;
  updateCurrentDepartmentFilter: (departmentFilter: string | null) => void;
  currentConnectorFilter: string | null;
  updateCurrentConnectorFilter: (connectorFilter: string | null) => void;
}

type FavoriteMap = {[key: string]: boolean};

const HatContext = createContext<HatContextProps | undefined>(undefined);

export const useHat = (): HatContextProps => {
  const context = useContext(HatContext);
  if (!context) {
    throw new Error("useHat must be used within a HatProvider");
  }
  return context;
};

interface HatProviderProps {
  children: React.ReactNode;
}

export const HatProvider: React.FC<HatProviderProps> = ({ children }) => {
  const [hats, setHats] = useState<Hat[]>([]);
  const [hatsList, setHatsList] = useState<Hat[]>([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<ErrorState | null>(null);
  const [selectedHat, setSelectedHatState] = useState<Hat | null>(null);
  // Bumped by every selection change. A restore still waiting on the VA list
  // only applies its answer if nothing has changed the selection since it began.
  const selectionVersionRef = useRef(0);

  const setSelectedHat = useCallback((hat: Hat | null) => {
    selectionVersionRef.current += 1;
    setSelectedHatState(hat);
  }, []);
  const [favoriteMap, setFavoriteMap] = useState<FavoriteMap>({});
  const [viewType, setViewType] = useState<string>('my_hats');
  const [pagination, setPagination] = useState<PaginationData>({
    page: 1,
    items_per_page: 10,
    total_pages: 1,
    has_next: false,
  });
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [initialPromptSent, setInitialPromptSent] = useState(false);
  const [filterLocked, setFilterLocked] = useState(false);

// filter state management
const [currentFilter, setCurrentFilterState] = useState<string>(() => {
  try {
    return localStorage.getItem('va-list-filter') || 'all';
  } catch {
    return 'all';
  }
});

// Function to update filter and persist
const updateCurrentFilter = useCallback((filter: string) => {
  setCurrentFilterState(filter);
  // Lock filter if it's not 'all'
  setFilterLocked(filter !== 'all');
  try {
    localStorage.setItem('va-list-filter', filter);
  } catch (error) {
    console.warn('Failed to save filter to localStorage:', error);
  }
}, []);

// department filter state management
const [currentDepartmentFilter, setCurrentDepartmentFilterState] = useState<string | null>(() => {
  try {
    const saved = localStorage.getItem('va-department-filter');
    return saved === 'null' ? null : saved;
  } catch {
    return null;
  }
});

// Function to update department filter and persist
const updateCurrentDepartmentFilter = useCallback((departmentFilter: string | null) => {
  setCurrentDepartmentFilterState(departmentFilter);
  try {
    localStorage.setItem('va-department-filter', departmentFilter || 'null');
  } catch (error) {
    console.warn('Failed to save department filter to localStorage:', error);
  }
}, []);

// connector filter state management
const [currentConnectorFilter, setCurrentConnectorFilterState] = useState<string | null>(() => {
  try {
    const saved = localStorage.getItem('va-connector-filter');
    return saved === 'null' ? null : saved;
  } catch {
    return null;
  }
});

// Function to update connector filter and persist
const updateCurrentConnectorFilter = useCallback((connectorFilter: string | null) => {
  setCurrentConnectorFilterState(connectorFilter);
  try {
    localStorage.setItem('va-connector-filter', connectorFilter || 'null');
  } catch (error) {
    console.warn('Failed to save connector filter to localStorage:', error);
  }
}, []);

  const { userId } = useUser();

  const resetError = useCallback(() => {
    setError(null);
  }, []);

  // Helper function to check if a hat is favorited (handles both DynamoDB formats)
  const isHatFavorited = useCallback((hat: Hat, userId: string) => {
    if (!hat.favorites || !userId) return false;
    
    const favoriteValue = hat.favorites[userId];
    
    if (typeof favoriteValue === 'object' && favoriteValue?.BOOL !== undefined) {
      return favoriteValue.BOOL === true;
    }
    
    return favoriteValue === true;
  }, []);

  // Updated fetchHatsList function
  const fetchHatsList = useCallback(async () => {
  // Skip if filter is locked for a specific view
  if (filterLocked && currentFilter !== 'all') {
    console.log('Skipping fetchHatsList - filter is locked to:', currentFilter);
    return;
  }

  if (!userId) return;
  try {
    setListLoading(true);
    setError(null);
    
    const response = await apiListHats();
    if (response) {
      setHatsList(response);
    } else {
      setHatsList([])
    }
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to fetch hats list';
    console.error("Failed to fetch hats list:", err);
    setError({ message: errorMessage });
    toast.error(errorMessage);
  } finally {
    setListLoading(false);
  }
}, [userId, filterLocked, currentFilter]);

  // function to fetch hats with filtering
  const fetchHatsListWithFilter = useCallback(async (filterType: string) => {
    if (!userId) return;
    
    try {
      setListLoading(true);
      setError(null);
      
      // Use the new paginated endpoint with filter
      const response = await apiListHatsPaginated(1, 50, filterType); 
      
      if (response && response.hats) {
        setHatsList(response.hats);
      } else {
        setHatsList([]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch filtered hats';
      console.error("Failed to fetch filtered hats:", err);
      setError({ message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setListLoading(false);
    }
  }, [userId]);

  const fetchHats = useCallback(
    async (userId: string, page: number, itemsPerPage: number, viewType: string = 'my_hats') => {
      if (!userId) return;
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Fetching hats - Page: ${page}, ViewType: ${viewType}`);
        
        const response = await apiListHatsPaginated(page, itemsPerPage, viewType);
        if (response && response.hats && response.pagination) {
          const newFavoriteMap = response.hats.reduce((acc: FavoriteMap, hat: Hat) => {
            acc[hat.id || ''] = isHatFavorited(hat, userId);
            return acc;
          }, {});
          setFavoriteMap(newFavoriteMap);

          const hatsWithFavorites = response.hats.map((hat: Hat) => ({
            ...hat,
            isFavorite: isHatFavorited(hat, userId)
          }));
          
          setHats(hatsWithFavorites);
          setPagination(response.pagination);
        } else {
          setHats([]) 
          setPagination({
            page: 1,
            items_per_page: 10,
            total_pages: 1,
            has_next: false,
          })
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch hats';
        console.error("Failed to fetch hats:", err);
        setError({ message: errorMessage });
        toast.error(errorMessage);
      } finally {
        setLoading(false);
        setInitialLoadComplete(true);
      }
    },
    [isHatFavorited]
  );

  const toggleFavorite = useCallback(async (hatId: string) => {
    if (!userId) return;

    try {
      // Get current favorite status
      const currentHat = hatsList.find(hat => hat.id === hatId);
      const currentFavoriteStatus = currentHat ? isHatFavorited(currentHat, userId) : false;
      const newFavoriteStatus = !currentFavoriteStatus;

      setHatsList(prevHatsList => 
        prevHatsList.map(hat => 
          hat.id === hatId 
            ? {
                ...hat,
                favorites: newFavoriteStatus 
                  ? { ...hat.favorites, [userId]: { BOOL: true } }
                  : { ...hat.favorites, [userId]: { BOOL: false } }
              }
            : hat
        )
      );

      setFavoriteMap(prev => ({
        ...prev,
        [hatId]: newFavoriteStatus
      }));

      setHats(prevHats => 
        prevHats.map(hat => 
          hat.id === hatId 
            ? {
                ...hat,
                isFavorite: newFavoriteStatus,
                favorites: newFavoriteStatus 
                  ? { ...hat.favorites, [userId]: { BOOL: true } }
                  : { ...hat.favorites, [userId]: { BOOL: false } }
              }
            : hat
        )
      );

      // Make API call
      if (newFavoriteStatus) {
        await apiAddToFavorites(hatId, true);
      } else {
        await apiRemoveFromFavorites(hatId);
      }
      
      toast.success(newFavoriteStatus ? 
        "Added to favorites!" : 
        "Removed from favorites!"
      );
    } catch (err) {
      // Revert the optimistic update on error
      const currentHat = hatsList.find(hat => hat.id === hatId);
      const originalStatus = currentHat ? isHatFavorited(currentHat, userId) : false;
      
      setFavoriteMap(prev => ({
        ...prev,
        [hatId]: originalStatus
      }));
      
      setHats(prevHats => 
        prevHats.map(hat => 
          hat.id === hatId 
            ? {
                ...hat,
                isFavorite: originalStatus,
                favorites: originalStatus 
                  ? { ...hat.favorites, [userId]: { BOOL: true } }
                  : { ...hat.favorites, [userId]: { BOOL: false } }
              }
            : hat
        )
      );

      const errorMessage = err instanceof Error ? err.message : 'Failed to update favorite';
      console.error("Error updating favorite:", err);
      setError({ message: errorMessage });
      toast.error(errorMessage);
    }
  }, [userId, hatsList, isHatFavorited]);

  // Sort hats with favorites first
  const sortedHats = useMemo(() => {
    return [...hats].sort((a, b) => {
      const aIsFavorite = favoriteMap[a.id || ''];
      const bIsFavorite = favoriteMap[b.id || ''];
      if (aIsFavorite && !bIsFavorite) return -1;
      if (!aIsFavorite && bIsFavorite) return 1;
      return 0;
    });
  }, [hats, favoriteMap]);

  // Initial load effect - with fewer dependencies
useEffect(() => {
  if (userId && !initialLoadComplete) {
    const initialLoad = async () => {
      await fetchHats(userId, pagination.page, pagination.items_per_page, viewType);
      if (currentFilter === 'all') {
        await fetchHatsList();
      } else {
        await fetchHatsListWithFilter(currentFilter);
      }
      setInitialLoadComplete(true);
    };
    
    initialLoad();
  }
}, [userId, initialLoadComplete]);

// Separate effect for filter changes only
useEffect(() => {
  if (userId && initialLoadComplete) {
    const loadFilteredData = async () => {
      if (currentFilter === 'all') {
        await fetchHatsList();
      } else {
        await fetchHatsListWithFilter(currentFilter);
      }
    };
    
    loadFilteredData();
  }
}, [currentFilter, userId, initialLoadComplete]); 

// Separate effect for pagination changes only
useEffect(() => {
  if (userId && initialLoadComplete) {
    fetchHats(userId, pagination.page, pagination.items_per_page, viewType);
  }
}, [pagination.page, pagination.items_per_page, viewType, userId, initialLoadComplete]); // Don't include function dependencies

  const setPage = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const createHat = useCallback(
    async (hatData: Hat) => {
      try {
        setLoading(true);
        setError(null);

        const payload = {
          user_id: userId,
          hat_name: hatData.hat_name,
          department: hatData.department || "", 
          summary: hatData.summary,
          initial_prompt: hatData.initial_prompt,
          directions: hatData.directions,
          is_public: hatData.is_public,
          feature_keys: hatData.feature_keys,
          model_id: hatData.model_id, 
        };

        console.log("Creating hat with payload:", payload);

        const newHat = await apiCreateHat(
          hatData.hat_name,
          hatData.summary,
          hatData.department || "",
          hatData.initial_prompt,
          hatData.directions,
          hatData.is_public,
          hatData.feature_keys,
          hatData.model_id  
        );

        if (!newHat || typeof newHat !== "object") {
          throw new Error("Invalid response from createHat");
        }

        await fetchHats(userId, pagination.page, pagination.items_per_page, viewType);
        await fetchHatsList();

        return newHat;

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create hat';
        console.error("Failed to create hat:", err);
        setError({ message: errorMessage });
        toast.error(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [userId, pagination.page, pagination.items_per_page, fetchHats, fetchHatsList, viewType]
  );

 const updateHatData = useCallback(
  async (hatId: string, updatedData: Partial<Hat>) => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("🔍 DEBUG: updateHatData called with:", updatedData);
      
      await apiUpdateHat(hatId, updatedData);
      
      // Add delay for department updates to avoid conflicts
      const hasDepartmentUpdate = 'department' in updatedData;
      
      if (hasDepartmentUpdate) {
        
        // Update local state optimistically first
        setHats((prevHats) => 
          prevHats.map((hat) => 
            hat.id === hatId 
              ? { ...hat, ...updatedData }
              : hat
          )
        );
        
        setHatsList((prevList) => 
          prevList.map((hat) => 
            hat.id === hatId 
              ? { ...hat, ...updatedData }
              : hat
          )
        );
        
        // Delayed refresh for department updates
        setTimeout(async () => {
          try {
            if (currentFilter === 'all') {
              const response = await apiListHats();
              if (response) {
                setHatsList(response);
              }
            } else {
              const response = await apiListHatsPaginated(1, 50, currentFilter);
              if (response && response.hats) {
                setHatsList(response.hats);
              }
            }
          } catch (error) {
            console.error("Error in delayed refresh:", error);
          }
        }, 1000);
        
      } else {
        if (currentFilter === 'all') {
          const response = await apiListHats();
          if (response) {
            setHatsList(response);
          }
        } else {
          const response = await apiListHatsPaginated(1, 50, currentFilter);
          if (response && response.hats) {
            setHatsList(response.hats);
          }
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update hat';
      console.error("Failed to update hat:", err);
      setError({ message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  },
  [currentFilter]
);
  const deleteHatById = useCallback(async (hatId: string) => {
    try {
      setLoading(true);
      setError(null);
      await apiDeleteHat(hatId);
      
      setHats((prevHats) => prevHats.filter((hat) => hat.id !== hatId));
      setHatsList((prevList) => prevList.filter((hat) => hat.id !== hatId));
      
      setFavoriteMap(prev => {
        const newMap = { ...prev };
        delete newMap[hatId];
        return newMap;
      });
      
      toast.success("Virtual Assistant deleted successfully!");
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete hat';
      console.error("Failed to delete hat:", err);
      setError({ message: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const removeHatFromDepartment = useCallback(async (hatId: string) => {
  try {
    setLoading(true);
    setError(null);
    
    // Call the API to remove department from hat
    await apiRemoveHatFromDepartment(hatId);
    
    // Update local state - remove department from the hat
    setHats((prevHats) => 
      prevHats.map((hat) => 
        hat.id === hatId 
          ? { ...hat, department: undefined }
          : hat
      )
    );
    
    setHatsList((prevList) => 
      prevList.map((hat) => 
        hat.id === hatId 
          ? { ...hat, department: undefined }
          : hat
      )
    );
    
    // Update selected hat if it's the one being modified
    if (selectedHat?.id === hatId) {
      setSelectedHat({ ...selectedHat, department: undefined });
    }
    
    // toast.success("Department removed from Virtual Assistant successfully!");
    
  } catch (err) {
    const errorMessage = err instanceof Error ? err.message : 'Failed to remove department from hat';
    console.error("Failed to remove department from hat:", err);
    setError({ message: errorMessage });
    toast.error(errorMessage);
    throw err;
  } finally {
    setLoading(false);
  }
}, [selectedHat]);

  // Show the VA a conversation is locked to when it is opened. hatsList can be
  // empty (still loading after a refresh) or hold only one filtered page of the
  // VA settings view, so a VA not found there is looked up in the full list
  // before being given up on. That lookup deliberately leaves hatsList alone, so
  // the settings view keeps the filter it is showing.
  const restoreSessionHat = useCallback(async (hatId?: string | null) => {
    const version = ++selectionVersionRef.current;

    // Older sessions can carry a non-id hat_id (a boolean true); no VA has it.
    if (typeof hatId !== "string" || !hatId) {
      setSelectedHatState(null);
      return;
    }

    const known = hatsList.find((hat) => hat.id === hatId);
    if (known) {
      setSelectedHatState(known);
      return;
    }

    // Nothing from the previous conversation is left showing while it loads.
    setSelectedHatState(null);
    try {
      const allHats: Hat[] | undefined = await apiListHats();
      if (version !== selectionVersionRef.current) return;
      setSelectedHatState(allHats?.find((hat) => hat.id === hatId) ?? null);
    } catch (err) {
      console.error("Failed to restore the conversation's VA:", err);
    }
  }, [hatsList]);

  const modifyHatWithLLM = useCallback(async (hatId: string, instructions: string) => {
    try {
      const result = await apiModifyHatWithLLM(hatId, instructions);

      // Update local state with the modified fields
      if (result && result.updated_fields) {
        setHats((prevHats) =>
          prevHats.map((hat) =>
            hat.id === hatId ? { ...hat, ...result.updated_fields } : hat
          )
        );
        setHatsList((prevList) =>
          prevList.map((hat) =>
            hat.id === hatId ? { ...hat, ...result.updated_fields } : hat
          )
        );
      }

      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to modify VA with AI';
      console.error("Failed to modify hat with LLM:", err);
      throw err;
    }
  }, []);

  return (
    <HatContext.Provider
      value={{
        hats: sortedHats,
        hatsList,
        selectedHat,
        setSelectedHat,
        restoreSessionHat,
        fetchHats,
        fetchHatsList,
        fetchHatsListWithFilter, 
        createHat,
        updateHatData,
        deleteHatById,
        removeHatFromDepartment,
        modifyHatWithLLM,
        loading,
        listLoading,
        error,
        pagination,
        setPage,
        setViewType,
        favoriteHats: Object.keys(favoriteMap).filter(id => favoriteMap[id]),
        toggleFavorite,
        resetError,
        viewType,
        initialPromptSent,
        setInitialPromptSent,
        currentFilter,
        filterLocked,
        updateCurrentFilter,
        currentDepartmentFilter,
        updateCurrentDepartmentFilter,
        currentConnectorFilter,
        updateCurrentConnectorFilter,
      }}
    >
      {children}
    </HatContext.Provider>
  );
};

export default HatProvider;